import React from "react";
import { Box, Container, Typography, Link, List, ListItem, Divider, Alert, Paper } from "@mui/material";

const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => {
    return (
        <Box id={id} sx={{ scrollMarginTop: 96 }}>
            <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
                {title}
            </Typography>
            <Box sx={{ color: "text.primary" }}>{children}</Box>
        </Box>
    );
};

const ToS: React.FC = () => {
    const lastUpdated = "October 23, 2025";
    const contactEmail = "miyu.apps@gmail.com";

    return (
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h3" component="h1" fontWeight={700} gutterBottom>
                        Terms of Service
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Last updated: {lastUpdated}
                    </Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 3 }}>
                    Welcome to CooCoo — a cozy space to explore, share, and create recipes together. By using CooCoo, you agree to these
                    Terms.
                </Alert>

                {/* Table of Contents */}
                <Box component="nav" aria-label="Terms of Service table of contents" sx={{ mb: 4 }}>
                    <Typography variant="overline" color="text.secondary">
                        Contents
                    </Typography>
                    <List dense sx={{ listStyleType: "decimal", pl: 3 }}>
                        {[
                            ["who-we-are", "Who We Are"],
                            ["using-coocoo", "Using CooCoo"],
                            ["your-content", "Your Content"],
                            ["intellectual-property", "Intellectual Property"],
                            ["privacy", "Privacy"],
                            ["service-changes", "Service Changes"],
                            ["advertising", "Advertising"],
                            ["termination", "Termination"],
                            ["liability", "Liability"],
                            ["governing-law", "Governing Law"],
                            ["changes-to-terms", "Changes to These Terms"],
                        ].map(([id, label]) => (
                            <ListItem key={id} sx={{ display: "list-item", py: 0 }}>
                                <Link href={`#${id}`} underline="hover" variant="body2">
                                    {label}
                                </Link>
                            </ListItem>
                        ))}
                    </List>
                </Box>

                <Section id="who-we-are" title="1. Who We Are">
                    <Typography>
                        CooCoo is operated by a private individual based in the Netherlands. If you have questions about these Terms,
                        contact us at <Link href={`mailto:${contactEmail}`}>{contactEmail}</Link>.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="using-coocoo" title="2. Using CooCoo">
                    <Typography sx={{ mb: 1 }}>
                        You may use CooCoo only if you are at least 13 years old and legally able to agree to these Terms.
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                        When you create an account, you agree to provide accurate information, keep your password secure, and take
                        responsibility for all activity under your account.
                    </Typography>
                    <Typography>
                        We reserve the right to suspend or delete accounts that violate these Terms or our community guidelines.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="your-content" title="3. Your Content">
                    <Typography sx={{ mb: 1 }}>
                        CooCoo lets you create and share content such as recipes, photos, and posts ("Your Content"). You own Your Content —
                        always.
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                        By posting on CooCoo, you grant us a limited, worldwide, non-exclusive license to display, store, and share Your
                        Content within the app and its features. This license ends when you delete Your Content or your account.
                    </Typography>
                    <Typography>
                        You’re responsible for ensuring Your Content doesn’t infringe others’ rights and doesn’t contain unlawful,
                        offensive, or harmful material. We may remove or restrict content that violates these guidelines.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="intellectual-property" title="4. Intellectual Property">
                    <Typography>
                        CooCoo’s name, logo, and platform design belong to us. You may not use them in a misleading or commercial way
                        without permission.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="privacy" title="5. Privacy">
                    <Typography>
                        Your privacy is very important to us. Please review our Privacy Policy to understand how we collect and use your
                        information.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="service-changes" title="6. Service Changes">
                    <Typography>
                        CooCoo is an evolving platform. We may update, add, or remove features from time to time. We’ll try to notify you of
                        major changes, but we can’t guarantee continuous, uninterrupted access to the service.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="advertising" title="7. Advertising">
                    <Typography>
                        CooCoo is currently ad-free, but we may introduce advertising in the future. Any ads will be clearly marked and will
                        never access your personal data without your consent.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="termination" title="8. Termination">
                    <Typography>
                        You can delete your account anytime. We may suspend or terminate your account if you violate these Terms or use the
                        service in a harmful way.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="liability" title="9. Liability">
                    <Typography>
                        CooCoo is provided “as is.” While we do our best to keep things running smoothly, we can’t guarantee that everything
                        will always work perfectly or be free from errors. We are not liable for any damages resulting from your use (or
                        inability to use) CooCoo.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="governing-law" title="10. Governing Law">
                    <Typography>
                        These Terms are governed by the laws of the Netherlands, and any disputes will be handled by Dutch courts.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="changes-to-terms" title="11. Changes to These Terms">
                    <Typography>
                        We may update these Terms from time to time. If we make significant changes, we’ll let you know via the app or email
                        before they take effect.
                    </Typography>
                </Section>

                <Divider sx={{ my: 4 }} />

                <Box>
                    <Typography variant="h6" gutterBottom>
                        Thank You 💛
                    </Typography>
                    <Typography>
                        CooCoo exists because of its community. Thanks for being part of it — and for keeping it a kind, creative space!
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default ToS;
